import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IFifthWheel, defaultValue } from 'app/shared/model/fifth-wheel.model';

const initialState: EntityState<IFifthWheel> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

interface IFifthWheelParams extends IQueryParams {
  truckImplementSearch?: string;
  codeSearch?: string;
  companyId?: number;
}

const apiUrl = 'api/fifth-wheels';

// Actions

export const getEntities = createAsyncThunk(
  'fifthWheel/fetch_entity_list',
  async ({ truckImplementSearch, codeSearch, companyId, page, size, sort }: IFifthWheelParams) => {
    const onTruck = truckImplementSearch ? `&truckImplementSearch=${truckImplementSearch.toUpperCase()}` : '';
    const onCode = codeSearch ? `&codeSearch=${codeSearch.toUpperCase()}` : '';
    const onCompanyId = companyId ? `&companyId=${companyId}` : '';
    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}${onCompanyId}${onCode}${onTruck}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IFifthWheel[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'fifthWheel/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IFifthWheel>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'fifthWheel/create_entity',
  async (entity: IFifthWheel, thunkAPI) => {
    const result = await axios.post<IFifthWheel>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'fifthWheel/update_entity',
  async (entity: IFifthWheel, thunkAPI) => {
    const result = await axios.put<IFifthWheel>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'fifthWheel/partial_update_entity',
  async (entity: IFifthWheel, thunkAPI) => {
    const result = await axios.patch<IFifthWheel>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'fifthWheel/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IFifthWheel>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const FifthWheelSlice = createEntitySlice({
  name: 'fifthWheel',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = FifthWheelSlice.actions;

// Reducer
export default FifthWheelSlice.reducer;
